import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  EmptyState,
  CircularLoader,
  Banner,
  ColumnsIcon,
  useDocumentTitle,
} from '@forge/common';
import { useConnections } from '@/hooks/useConnections';
import { usePartners } from '@/hooks/usePartners';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';

const ConnectionsPage = () => {
  useDocumentTitle('Connections — Partner Exchange — OneRing Portal');
  const navigate = useNavigate();

  const { partners } = usePartners();
  const { connections, isLoading, error, isUsingMockData } = useConnections();

  const partnerNameById = useMemo(
    () => new Map(partners.map((p) => [p.id, p.displayName])),
    [partners],
  );

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">Connections</h2>
      <p className="text-sm text-color-tertiary mb-4">
        All SFTP endpoints configured across every partner. Open a partner's detail page to add
        or edit its connections.
      </p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="Connections" />
        </div>
      )}

      {error ? (
        <Banner
          variant="inline"
          type="error"
          title="Failed to load connections"
          description={error}
          canClose={false}
        />
      ) : (
        <Card>
          <CardHeader
            title="Connections"
            subtitle={`${connections.length} connection${connections.length !== 1 ? 's' : ''}`}
          />
          <CardContent>
            {isLoading ? (
              <div className="flex min-h-[240px] items-center justify-center">
                <CircularLoader size="large" />
              </div>
            ) : connections.length === 0 ? (
              <EmptyState icon={<ColumnsIcon />} description="No connections configured" />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Partner</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Host</TableCell>
                    <TableCell>Last Test</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connections.map((conn) => (
                    <TableRow
                      key={conn.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => navigate(`/partners/${conn.partnerId}`)}
                    >
                      <TableCell>{partnerNameById.get(conn.partnerId) ?? conn.partnerId}</TableCell>
                      <TableCell>{conn.label}</TableCell>
                      <TableCell>
                        <Badge color="blue">{conn.direction}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono">
                          {conn.host}:{conn.port}
                        </span>
                      </TableCell>
                      <TableCell>
                        {conn.lastTestStatus ? (
                          <Badge color={conn.lastTestStatus === 'ok' ? 'green' : 'red'}>
                            {conn.lastTestStatus}
                          </Badge>
                        ) : (
                          <span className="text-xs text-color-tertiary">Never tested</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConnectionsPage;
